import lodashToString from 'lodash-es/toString';
import type { NavigationGuardReturn, RouteLocationNormalizedLoaded, RouteLocationRaw } from 'vue-router';

import { apiGetOrderBuyerDetail, apiGetValueAddedServices, SellingMethod } from '~/apiClient';
import { ROUTE_NAMES } from '~/constants/routes';
import { isSaleOrderItemEntity } from '~/types/order.type';

const PURCHASE_FLOW_ROUTE_PARAM = 'purchase-flow';

type UseOrderPurchaseFlowGuardReturn = {
    isPurchaseFlow: ComputedRef<boolean>;
    formatPurchaseFlowRoute: (params: { orderId: string }) => RouteLocationRaw;
    checkPurchaseFlowAndRedirect: () => Promise<NavigationGuardReturn>;
};

export const useOrderPurchaseFlowGuard = (routeArg?: RouteLocationNormalizedLoaded): UseOrderPurchaseFlowGuardReturn => {
    const route = routeArg ?? useRoute();
    const routeBaseName = useRouteBaseName();
    const routeName = routeBaseName(route);
    const localePath = useLocalePath();
    const { logError } = useLogs();

    const isPurchaseFlow = computed(() => routeName === ROUTE_NAMES.ACCOUNT_ORDER_EDIT && route.query[PURCHASE_FLOW_ROUTE_PARAM] !== undefined);

    const formatPurchaseFlowRoute = ({ orderId }: { orderId: string }): ReturnType<UseOrderPurchaseFlowGuardReturn['formatPurchaseFlowRoute']> => {
        return localePath({
            name: ROUTE_NAMES.ACCOUNT_ORDER_EDIT,
            params: { id: orderId },
            query: { [PURCHASE_FLOW_ROUTE_PARAM]: null },
        });
    };

    const checkPurchaseFlowAndRedirect = async (): ReturnType<UseOrderPurchaseFlowGuardReturn['checkPurchaseFlowAndRedirect']> => {
        if (!isPurchaseFlow.value) return;

        const orderId = lodashToString(route.params.id);

        try {
            const orderResponse = await apiGetOrderBuyerDetail(orderId);
            const saleOrderItem = orderResponse.order.orderItems.find(item => isSaleOrderItemEntity(item));

            if (!saleOrderItem) return;

            const { saleReference } = saleOrderItem;
            const valueAddedServices = await apiGetValueAddedServices({
                [saleReference.saleType === SellingMethod.Auction ? 'auctionId' : 'directSaleId']: saleReference.saleId,
            });

            if (valueAddedServices.every(vas => !vas.enabled || !vas.purchasable)) {
                return localePath({
                    name: ROUTE_NAMES.ACCOUNT_ORDER_THANK_YOU,
                    params: { id: orderId },
                });
            }
        } catch (error) {
            logError('Error fetching order data in order purchase flow guard', { error });

            return;
        }
    };

    return {
        isPurchaseFlow,
        formatPurchaseFlowRoute,
        checkPurchaseFlowAndRedirect,
    };
};
